import { useMutation, useQuery } from '@tanstack/react-query'
import { get } from 'lodash'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { GeneralResMock } from '../types/general.type'
import { MedFrequencyResMock } from '../types/med_frequency.type'
import { MedicationDataMock, MedicationIncluded, Meta4 } from '../types/medication.type'
import { FamilyProps } from './useFamily'
import { useMedicationOrdersSubscription } from './useMedicationOrdersSubscription'

export const useMedication = (arg?: FamilyProps) => {
  useMedicationOrdersSubscription()

  const patientId = arg?.patientId

  const id = arg?.id
  const query = arg?.query
  const date = arg?.date
  const limit = arg?.limit
  const page = arg?.page
  const sortDir = arg?.sortDir || 'desc'
  const sortBy = arg?.sortBy
  const status = arg?.status ? arg.status : []
  const encounterId = arg?.encounterId
  const patient = arg?.patient
  const prescribed = arg?.prescribed
  const reconciliation = arg?.reconciliation
  const intakeId = arg?.intakeId
  const encounterType = arg?.encounter_type
  const prescribedKey = prescribed === true ? true : 'all'
  const context = arg?.context
  const stat = arg?.stat || true

  const {
    data: medications,
    isLoading: medicationIsLoading,
    refetch: refetchMedications
  } = useQuery({
    queryFn: async () => {
      let endpoint = `/medications?`
      if (sortDir) endpoint += `sort_dir=${sortDir}&`
      if (sortBy) endpoint += `sort_by=${sortBy}&`
      if (patientId) endpoint += `patient_id=${patientId}&`
      if (limit) endpoint += `limit=${limit}&`
      if (encounterId) endpoint += `encounter_id=${encounterId}&`
      if (intakeId) endpoint += `intake_id=${intakeId}&`
      if (context) endpoint += `context=${context}&`
      if (encounterType) endpoint += `encounter_type=${encounterType}&`
      if (page) endpoint += `page%5Bnumber%5D=${page}&page%5Bsize%5D=${limit}&`
      if (stat !== undefined) endpoint += `stat=${stat}&`
      if (query) endpoint += `query=${query}&`
      if (date) endpoint += `date=${date}&`
      if (status && status?.length > 0) {
        status.forEach((s: string) => {
          endpoint += `status[]=${s}&`
        })
      }
      if (prescribed !== undefined) endpoint += `prescribed=${prescribed}&`
      if (reconciliation !== undefined) endpoint += `reconciliation=${reconciliation}&`
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<MedicationDataMock[], MedicationIncluded[]>>(endpoint)
      return data
    },
    queryKey: [
      'medication',
      {
        context,
        encounterId,
        intakeId,
        limit,
        page,
        patientId,
        prescribed,
        prescribedKey,
        query,
        reconciliation,
        sortBy,
        sortDir,
        status,
        encounterType,
        date
      }
    ]
  })

  const { data: patientMedication } = useQuery({
    enabled: !!patient,
    queryFn: async () => {
      let endpoint = `/medications?status[]=ongoing&page%5Bnumber%5D=${1}&page%5Bsize%5D=${50}&`
      if (sortDir) endpoint += `sort_dir=${sortDir}&`
      if (patient) endpoint += `patient_id=${patient}&`
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<MedicationDataMock[], MedicationIncluded[]>>(endpoint)
      return data
    },
    queryKey: ['medication', { patient }]
  })

  const { data: medication, isLoading: singleMedIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<MedicationDataMock, MedicationIncluded[]>>(
        `/medications/${id}`
      )
      return data
    },
    queryKey: ['medication', { id }]
  })

  const { mutateAsync: createMedication } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post(`/medications`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medication'] })
      queryClient.invalidateQueries({ queryKey: ['patient_medications'] })
      queryClient.invalidateQueries({ queryKey: ['medication_administration'] })
    }
  })

  const { mutateAsync: updateMedication } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      await AXIOS_INSTANCE.patch(`medications/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['medication'] })
      queryClient.invalidateQueries({ queryKey: ['medication', { id }] })
      queryClient.invalidateQueries({ queryKey: ['patient_medications'] })
    }
  })

  const { mutateAsync: updateMedicationStatus } = useMutation({
    mutationFn: async ({
      payload,
      id
    }: {
      payload: { status: MedicationDataMock['meta']['status']; product_id?: string; note?: string }
      id: string
    }) => {
      await AXIOS_INSTANCE.patch(`medications/${id}/update_status`, payload, {
        headers: { 'Content-Type': 'application/json' }
      })
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['medication'] })
      queryClient.invalidateQueries({ queryKey: ['medication', { id }] })
      queryClient.invalidateQueries({ queryKey: ['patient_medications'] })
      queryClient.invalidateQueries({ queryKey: ['medication_administration'] })
    }
  })

  const { data: med_frequencies, isLoading: medFreqsIsLoading } = useQuery({
    queryFn: async () => {
      let endpoint = 'medication_frequencies?'
      if (query) endpoint += `query=${query}&`
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<MedFrequencyResMock[]>>(endpoint)
      return data
    },
    queryKey: ['med_frequencies', { id, query }]
  })

  const { mutateAsync: deleteMedication } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await AXIOS_INSTANCE.delete(`medications/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medication'] })
      queryClient.invalidateQueries({ queryKey: ['patient_medications'] })
      queryClient.invalidateQueries({ queryKey: ['medication_administration'] })
    }
  })
  return {
    createMedication,
    deleteMedication,
    includedData: medications?.included,
    includedMed: get(medication, 'included'),
    medFreqsIsLoading,
    medFrequencies: get(med_frequencies, 'data'),
    medication: medication?.data,
    medicationIsLoading,
    medications: medications?.data,
    pagination: medications?.meta.pagination,
    medStats: medications?.meta.stats as Meta4['stats'],
    patientMedication,
    refetchMedications,
    singleMedIsLoading,
    updateMedication,
    updateMedicationStatus
  }
}
